import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, MapPin, Waves, Landmark, Palette, TreePine, BedDouble } from 'lucide-react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';

const ARTICLE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Travel Guide: Kannur — Kerala\'s Cultural Crown on the Malabar Coast',
  description:
    'Complete travel guide to Kannur, Kerala. Explore Theyyam rituals, pristine beaches, St. Angelo Fort, spice heritage, and retiring room options at Kannur Railway Station.',
  image: 'https://upload.wikimedia.org/wikipedia/commons/f/f1/Fisherman_at_Muzhappilangad_Beach.jpg',
  author: { '@type': 'Organization', name: 'Retiring Room' },
  publisher: {
    '@type': 'Organization',
    name: 'Retiring Room',
    logo: { '@type': 'ImageObject', url: 'https://retiringroom.in/logo.png' },
  },
  datePublished: '2026-03-20',
  dateModified: '2026-03-20',
  mainEntityOfPage: 'https://retiringroom.in/blog/travel-guide-kannur',
};

const BlogKannur = () => {
  return (
    <PublicLayout>
      <SEO
        title="Travel Guide: Kannur — Kerala's Cultural Crown on the Malabar Coast"
        description="Complete travel guide to Kannur, Kerala. Explore Theyyam rituals, pristine beaches, St. Angelo Fort, spice heritage, and retiring room options at Kannur Railway Station."
        canonical="/blog/travel-guide-kannur"
        ogType="article"
        ogImage="https://upload.wikimedia.org/wikipedia/commons/f/f1/Fisherman_at_Muzhappilangad_Beach.jpg"
        keywords="Kannur travel guide, Kannur tourism, Kannur Kerala, Theyyam Kannur, Muzhappilangad Beach, St Angelo Fort, Malabar coast, Kannur retiring room, Kannur railway station, Kerala tourism, Kannur things to do"
        jsonLd={ARTICLE_JSON_LD}
      />

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <img
          src="https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=1400&q=80"
          alt="Beautiful coastal landscape of Kannur, Kerala — known for pristine beaches and the Malabar coast heritage"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to Blog
          </Link>
          <span className="inline-block bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">Kerala</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-white leading-tight">
            Travel Guide: Kannur — Kerala's Cultural Crown
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-white/60">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" aria-hidden="true" />
              March 20, 2026
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" aria-hidden="true" />
              10 min read
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" aria-hidden="true" />
              Kannur, Kerala
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">

            {/* Introduction */}
            <section className="mb-12">
              <p className="text-lg text-midnight-700 leading-relaxed">
                Kannur (also known as Cannanore) is a vibrant coastal city on Kerala's northern Malabar coast,
                steeped in centuries of history, art, and natural beauty. Often called the <strong>"Land of Looms and Lores,"</strong>
                Kannur is where ancient traditions blend seamlessly with stunning landscapes — from the mystical Theyyam
                rituals performed in sacred groves to the pristine beaches that stretch along the Arabian Sea.
              </p>
              <p className="text-midnight-600 leading-relaxed mt-4">
                As one of Kerala's most culturally rich yet under-explored destinations, Kannur offers an authentic
                experience far removed from the tourist crowds. Whether you arrive by train at Kannur Railway Station
                or via the recently expanded Kannur International Airport, this city promises an unforgettable journey
                into the heart of Malabar heritage.
              </p>
            </section>

            {/* Theyyam */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Palette className="w-5 h-5 text-green-600" aria-hidden="true" />
                </div>
                <h2 className="text-2xl font-display font-bold text-midnight-900 m-0">Theyyam — The Dance of the Gods</h2>
              </div>
              <p className="text-midnight-600 leading-relaxed">
                Theyyam is Kannur's most extraordinary cultural treasure — a ritualistic art form that is over
                <strong> 800 years old</strong> and predates the classical dance forms of Kerala. Performed in <em>kavus</em> (sacred groves)
                and temple courtyards, Theyyam features elaborately costumed performers who are believed to embody
                deities and ancestral heroes.
              </p>
              <div className="mt-6 bg-green-50 rounded-2xl p-6">
                <h3 className="font-display font-semibold text-midnight-900 mb-3">Theyyam Highlights</h3>
                <ul className="space-y-2">
                  {[
                    'Over 400 different forms of Theyyam have been identified, each with unique costumes, music, and choreography',
                    'The Theyyam season runs from October to May — the best time to witness these rituals in Kannur',
                    'Major Theyyam temples include Parassinikadavu Muthappan Temple, Andalurkavu, and Kunnathurpadi',
                    'Unlike classical performances, Theyyam is a living ritual — performers enter a trance state and bless devotees',
                    'Photography is generally allowed but ask permission first; maintain respectful silence during the ritual',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-midnight-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" aria-hidden="true"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-midnight-600 leading-relaxed mt-4">
                Watching a Theyyam performance is a transformative experience. The combination of rhythmic drumming,
                vibrant face paintings, towering headdresses, and the spiritual energy of the ritual creates an atmosphere
                unlike anything else in India.
              </p>
            </section>

            {/* Beaches */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Waves className="w-5 h-5 text-green-600" aria-hidden="true" />
                </div>
                <h2 className="text-2xl font-display font-bold text-midnight-900 m-0">Pristine Beaches of Kannur</h2>
              </div>
              <p className="text-midnight-600 leading-relaxed">
                Kannur boasts some of Kerala's most beautiful and uncrowded beaches. Unlike the heavily touristed beaches
                of South Kerala, Kannur's coastline retains a raw, untouched charm that makes every beach visit feel special.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {[
                  {
                    title: 'Muzhappilangad Drive-in Beach',
                    description:
                      'India\'s longest drive-in beach, stretching 4 km along the coast. You can literally drive your car on the firm, golden sand right beside the waves. Recognized by BBC as one of the world\'s top six drive-in beaches.',
                    image: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600&q=80',
                    imageAlt: 'Muzhappilangad Drive-in Beach in Kannur, Kerala — India\'s longest drive-in beach',
                  },
                  {
                    title: 'Payyambalam Beach',
                    description:
                      'A serene beach in the heart of Kannur city with a beautiful promenade, well-maintained gardens, and a striking sculpture park. Perfect for evening walks and watching spectacular sunsets over the Arabian Sea.',
                    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80',
                    imageAlt: 'Sunset at Payyambalam Beach in Kannur with golden sand and calm Arabian Sea waves',
                  },
                  {
                    title: 'Thottada Beach',
                    description:
                      'A hidden gem where the Thottada River meets the sea, creating a unique estuarine landscape. The beach is flanked by coconut palms and offers a peaceful, almost private beach experience.',
                    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&q=80',
                    imageAlt: 'Thottada Beach in Kannur where the river meets the sea surrounded by coconut palms',
                  },
                  {
                    title: 'Meenkunnu Beach',
                    description:
                      'Known for its dramatic rock formations and clear turquoise waters. Quieter than other beaches, Meenkunnu is ideal for those seeking solitude and natural beauty away from tourist spots.',
                    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=600&q=80',
                    imageAlt: 'Rocky coastline and turquoise waters at Meenkunnu Beach in Kannur, Kerala',
                  },
                ].map((beach) => (
                  <div key={beach.title} className="bg-midnight-50 rounded-2xl overflow-hidden">
                    <img
                      src={beach.image}
                      alt={beach.imageAlt}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                    />
                    <div className="p-5">
                      <h3 className="font-display font-semibold text-lg text-midnight-900">{beach.title}</h3>
                      <p className="text-sm text-midnight-600 mt-2 leading-relaxed">{beach.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Historical Landmarks */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-green-600" aria-hidden="true" />
                </div>
                <h2 className="text-2xl font-display font-bold text-midnight-900 m-0">Historical Landmarks</h2>
              </div>
              <p className="text-midnight-600 leading-relaxed">
                Kannur's strategic coastal position made it a coveted prize for colonial powers. Portuguese, Dutch, and
                British forces all left their mark on the city, resulting in a fascinating architectural and cultural heritage.
              </p>
              <div className="mt-6 space-y-6">
                {[
                  {
                    title: 'St. Angelo Fort (1505)',
                    description:
                      'Built by the Portuguese Viceroy Dom Francisco de Almeida, this well-preserved laterite fort overlooks the Arabian Sea and offers panoramic views. It was later occupied by the Dutch and the British. The fort is a protected monument under the Archaeological Survey of India.',
                  },
                  {
                    title: 'Arakkal Museum',
                    description:
                      'The Arakkal family was the only Muslim royal family in Kerala, and their palace-turned-museum showcases royal artifacts, historical documents, coins, and weaponry from the Arakkal dynasty that ruled the Laccadive Islands.',
                  },
                  {
                    title: 'Thalassery Fort (25 km)',
                    description:
                      'Built by the British East India Company in 1708, this fort in nearby Thalassery is where cricket was first played in India. The town is also famous for Thalassery biryani — a unique Malabar version unlike any other.',
                  },
                ].map((landmark) => (
                  <div key={landmark.title} className="flex items-start gap-4 bg-midnight-50 rounded-xl p-5">
                    <span className="w-3 h-3 bg-green-500 rounded-full mt-2 flex-shrink-0" aria-hidden="true"></span>
                    <div>
                      <h3 className="font-display font-semibold text-midnight-900">{landmark.title}</h3>
                      <p className="text-sm text-midnight-600 mt-1 leading-relaxed">{landmark.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Nature & Wildlife */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TreePine className="w-5 h-5 text-green-600" aria-hidden="true" />
                </div>
                <h2 className="text-2xl font-display font-bold text-midnight-900 m-0">Nature & Wildlife</h2>
              </div>
              <p className="text-midnight-600 leading-relaxed">
                Beyond its beaches and cultural heritage, Kannur district is blessed with lush forests, rolling hills,
                and rich biodiversity. The Western Ghats that form the eastern boundary of the district are home to
                several remarkable natural attractions.
              </p>
              <ul className="mt-4 space-y-3">
                {[
                  {
                    name: 'Aralam Wildlife Sanctuary',
                    desc: 'The largest wildlife sanctuary in North Kerala, spread across 55 sq km of tropical forests. Home to elephants, bison, deer, and over 200 bird species.',
                  },
                  {
                    name: 'Paithalmala',
                    desc: 'A trekking destination at 1,372 meters altitude offering breathtaking views of the Western Ghats. The meadows at the summit are often shrouded in mist.',
                  },
                  {
                    name: 'Pythal Mala Peak',
                    desc: 'One of the highest peaks in the Kannur district with grasslands, shola forests, and rare orchid species. A paradise for trekkers and botanists.',
                  },
                  {
                    name: 'Kavvayi Island & Backwaters',
                    desc: 'Kerala\'s largest river island, surrounded by serene backwaters. Boat rides here offer a quiet, uncrowded alternative to the famous Alleppey backwaters.',
                  },
                ].map((item) => (
                  <li key={item.name} className="flex items-start gap-3 text-midnight-600">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" aria-hidden="true"></span>
                    <div>
                      <strong className="text-midnight-900">{item.name}</strong> — {item.desc}
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* Malabar Cuisine */}
            <section className="mb-12">
              <h2 className="text-2xl font-display font-bold text-midnight-900 mb-4">Malabar Cuisine — A Food Lover's Paradise</h2>
              <p className="text-midnight-600 leading-relaxed">
                Kannur's culinary scene is dominated by the rich and aromatic <strong>Malabar cuisine</strong>,
                known for its generous use of coconut, spices, and seafood. The food here reflects the region's
                diverse cultural influences — Arab, Portuguese, and local Kerala traditions.
              </p>
              <div className="mt-6 bg-green-50 rounded-2xl p-6">
                <h3 className="font-display font-semibold text-midnight-900 mb-4">Must-Try Malabar Dishes</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { name: 'Thalassery Biryani', desc: 'A unique biryani made with kaima rice, distinct from Hyderabadi and Lucknowi styles' },
                    { name: 'Malabar Parotta & Curry', desc: 'Flaky, layered flatbread with rich coconut-based meat or fish curry' },
                    { name: 'Kannur Fish Fry', desc: 'Fresh catch marinated in red spice paste and shallow-fried to perfection' },
                    { name: 'Pathiri', desc: 'Thin rice flour bread traditionally served with chicken or egg curry' },
                    { name: 'Unnakaya', desc: 'Sweet banana rolls stuffed with cashew, raisins, and coconut' },
                    { name: 'Kallummakkaya Fry', desc: 'Spicy fried mussels — a coastal Malabar street food staple' },
                  ].map((dish) => (
                    <div key={dish.name} className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" aria-hidden="true"></span>
                      <div>
                        <span className="font-semibold text-midnight-900">{dish.name}</span>
                        <span className="text-midnight-500 text-sm"> — {dish.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Retiring Room CTA */}
            <section className="mb-8">
              <div className="bg-gradient-to-br from-midnight-900 to-midnight-950 text-white rounded-2xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BedDouble className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="text-xl font-display font-bold">Stay at Kannur Retiring Room</h2>
                    <p className="text-white/70 mt-2 leading-relaxed">
                      Arriving at Kannur Railway Station? Book a comfortable retiring room right at the station for a
                      refreshing rest before exploring the Malabar coast. AC rooms, Non-AC rooms, and dormitory beds
                      available with flexible time slots to suit your travel schedule.
                    </p>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 mt-4 bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all"
                    >
                      Book a Room at Kannur
                      <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {/* Travel Tips */}
            <section className="mb-12">
              <h2 className="text-2xl font-display font-bold text-midnight-900 mb-4">Travel Tips for Kannur</h2>
              <div className="bg-midnight-50 rounded-2xl p-6 space-y-3">
                {[
                  'Best time to visit: October to March for pleasant weather and the Theyyam season',
                  'Kannur International Airport (CNN) offers direct flights from major Indian cities and Gulf countries',
                  'Kannur Railway Station is well-connected with trains to Mangalore, Kochi, Thiruvananthapuram, and Coimbatore',
                  'Rent a scooter or hire a local taxi for exploring beaches and nearby attractions at your own pace',
                  'Carry light cotton clothing — Kannur has a tropical climate with high humidity year-round',
                  'Book your retiring room in advance during Theyyam season (October–May) when tourism peaks',
                  'Visit Muzhappilangad Beach during low tide for the best driving experience on the sand',
                  'Don\'t miss the Kannur handloom cooperatives — famous for Kannur\'s high-quality handloom textiles',
                ].map((tip, index) => (
                  <div key={index} className="flex items-start gap-3 text-midnight-600">
                    <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </section>

          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-midnight-200">
            <Link to="/blog/travel-guide-bhusaval" className="inline-flex items-center gap-2 text-midnight-600 hover:text-railway-500 font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Bhusaval Guide
            </Link>
            <Link to="/blog/travel-guide-kottayam" className="inline-flex items-center gap-2 text-railway-500 hover:text-railway-600 font-medium transition-colors">
              Kottayam Guide
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </article>
    </PublicLayout>
  );
};

export default BlogKannur;
